/**
 * Die Wörter und Sätze eines Ladepunkts (HM-119, ADR 0036).
 *
 * **Gedeutet hat die API** — welcher Schlüssel den Modus trägt, was `2` darin
 * heißt, wer steuern darf und warum es gerade nicht geht. Hier steht nur, wie
 * das auf Deutsch heißt, und zwar **zweimal**: Am Gerät stehen die Begriffe von
 * evcc, weil dort jemand einrichtet, der sie kennt; auf „Laden" steht, was
 * passiert. Es sind dieselben vier Betriebsarten — nicht vier weitere.
 *
 * **Was nicht gemeldet ist, wird nicht gerechnet.** Der Entwurf zeigt eine
 * Fertigzeit, einen Sonnenanteil der Ladeleistung und ein Fahrzeug mit Namen.
 * Seit HM-165 meldet das Gateway die Restladezeit, den Solaranteil der Sitzung
 * und die Fahrzeuge — gezeigt wird, **was evcc ausrechnet**, und nichts, was
 * hier aus einer angenommenen Batteriegröße entstünde (Regel 9). Eine Fertigzeit
 * gibt es deshalb nur als „noch 1 h 20 min" aus `remainingDurationS`, nicht als
 * Uhrzeit, die aussieht wie eine Zusage.
 */
import { formatInstant } from "@hausmeter/i18n";
import type { ApiComponents } from "@hausmeter/sdk";

export type ChargePoint = ApiComponents["schemas"]["ChargePoint"];
export type ChargePointChange = ApiComponents["schemas"]["ChargePointChange"];
export type ChargePointCommand = ApiComponents["schemas"]["ChargePointCommand"];
export type ChargeMode = NonNullable<ChargePoint["state"]["mode"]["value"]>;
export type AlwaysCharge = NonNullable<ChargePoint["state"]["alwaysCharge"]["value"]>;
export type ChargePointSetting = ChargePoint["controllableSettings"][number];
type Status = NonNullable<ChargePoint["state"]["status"]["value"]>;
type Block = NonNullable<ChargePoint["controlBlockedBy"]>;

export interface ChargeModeText {
  readonly mode: ChargeMode;
  /** Der Begriff von evcc — am Gerät. */
  readonly label: string;
  readonly text: string;
  /** Was passiert — auf „Laden". */
  readonly plain: string;
  readonly plainText: string;
}

/**
 * In der Reihenfolge, in der man auf „Laden" wählt: der Alltagsfall zuerst,
 * die Pause zuletzt. Am Gerät gilt die Reihenfolge der Wirkung (`TECHNICAL_ORDER`).
 */
export const CHARGE_MODES: readonly ChargeModeText[] = [
  {
    mode: "minpv",
    label: "Min + PV",
    text: "Lädt mindestens mit dem Mindeststrom und darüber hinaus nur mit Überschuss.",
    plain: "Sonne bevorzugt",
    plainText:
      "Lädt immer ein wenig weiter und nimmt darüber hinaus, was die Anlage an Überschuss liefert. Der Alltagsfall.",
  },
  {
    mode: "pv",
    label: "Nur Überschuss",
    text: "Lädt ausschließlich mit Überschuss. Reicht er nicht für den Mindeststrom, pausiert die Ladung.",
    plain: "Nur Sonne",
    plainText:
      "Lädt nur mit Überschuss der Anlage. Reicht er nicht, pausiert die Ladung und geht später von selbst weiter.",
  },
  {
    mode: "now",
    label: "Sofort",
    text: "Lädt mit dem Maximalstrom, unabhängig von der Erzeugung — Netzbezug inbegriffen.",
    plain: "Schnell laden",
    plainText:
      "Volle Leistung, so schnell es geht. Was die Anlage nicht liefert, kommt aus dem Netz.",
  },
  {
    mode: "off",
    label: "Aus",
    text: "Der Ladepunkt gibt keine Leistung frei. Ein angestecktes Fahrzeug wartet.",
    plain: "Pause",
    plainText: "Es wird nichts geladen. Das Fahrzeug kann angesteckt bleiben.",
  },
];

export const TECHNICAL_ORDER: readonly ChargeMode[] = ["off", "now", "minpv", "pv"];

export function modeText(mode: ChargeMode): ChargeModeText {
  const found = CHARGE_MODES.find((entry) => entry.mode === mode);
  if (found === undefined) throw new Error(`Unbekannte Betriebsart ${mode}`);
  return found;
}

export const STATUS_BADGES: Readonly<
  Record<Status, { label: string; tone: "neutral" | "info" | "success" }>
> = {
  disconnected: { label: "Kein Fahrzeug", tone: "neutral" },
  connected: { label: "Fahrzeug angesteckt", tone: "info" },
  charging: { label: "Lädt", tone: "success" },
};

/** Warum gerade nicht bedient werden kann — ein Satz je Grund, für jeden Aufrufer gleich. */
export const BLOCK_TEXTS: Readonly<Record<Block, { title: string; text: string }>> = {
  unreachable: {
    title: "Keine Steuerung ohne Verbindung",
    text: "Der Ladepunkt ist gerade nicht verbunden. Aufträge werden nicht zurückgehalten — ein Ladebefehl, der Stunden später ausgeführt wird, ist keine Bedienung. Bis dahin lädt er nach der zuletzt bestätigten Betriebsart.",
  },
  never_seen: {
    title: "Noch kein Stand gemeldet",
    text: "Der Ladepunkt hat sich noch nie gemeldet. Bedienen lässt er sich, sobald sein erster Stand angekommen ist.",
  },
  command_pending: {
    title: "Änderung unterwegs",
    text: "Der Ladepunkt hat die letzte Änderung noch nicht bestätigt. Bis dahin gilt, was er zuletzt gemeldet hat — ein zweiter Auftrag ginge verloren.",
  },
};

/** Ob ein Grund dauerhaft sperrt (Hinweis oben) oder nur kurz (Zeile unten). */
export function isHardBlock(block: ChargePoint["controlBlockedBy"]): boolean {
  return block === "unreachable" || block === "never_seen";
}

const PHASE_LABELS: Readonly<Record<number, string>> = {
  0: "automatisch",
  1: "einphasig",
  3: "dreiphasig",
};

export function phaseLabel(phases: number): string {
  return PHASE_LABELS[phases] ?? `${phases} Phasen`;
}

/** Anteil 0…1 als ganze Prozent — ein Ladeziel von 0,8 steht als „80 %". */
export function percentOf(ratio: number): number {
  return Math.round(ratio * 100);
}

/** Ob dieser Aufrufer eine Einstellung setzen darf — entschieden hat die API (HM-165). */
export function maySet(point: ChargePoint, setting: ChargePointSetting): boolean {
  return point.controllableSettings.includes(setting);
}

/**
 * „Immer mit Mindestleistung" (HM-165, evcc ≥ 0.316). Seit 0.316 ist „Min + PV"
 * in evcc der Sonnenbetrieb mit dieser Einstellung an — und „bis zum Abstecken"
 * erscheint deshalb ebenfalls als „Min + PV", bis das Fahrzeug abgesteckt ist.
 */
export const ALWAYS_CHARGE_TEXTS: Readonly<Record<AlwaysCharge, { label: string; text: string }>> =
  {
    off: { label: "Aus", text: "Im Sonnenbetrieb wird nur mit Überschuss geladen." },
    on: {
      label: "Immer",
      text: "Im Sonnenbetrieb lädt das Fahrzeug immer mindestens mit dem Mindeststrom.",
    },
    once: {
      label: "Bis zum Abstecken",
      text: "Wie „Immer“, aber nur für dieses Fahrzeug: Nach dem Abstecken gilt wieder „Aus“.",
    },
  };

/** Worauf die Wallbox wartet — ein Satz, nur wenn sie wartet. */
export const WAITING_TEXTS: Readonly<Record<"authorization" | "disconnect_required", string>> = {
  authorization: "Wartet auf Freigabe (RFID-Karte)",
  disconnect_required: "Wartet aufs Abstecken, bevor es weitergeht",
};

/**
 * Eine Dauer in Worten — „45 min", „1 h 20 min". Sekunden zeigt niemand: Eine
 * Restladezeit auf die Sekunde ist eine Genauigkeit, die evcc nicht hat.
 */
export function formatDuration(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 1) return "unter 1 min";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/**
 * Ein Planzeitpunkt in der Zeitzone der Anlage. Eine Uhrzeit `HH:MM` steht nur
 * in einem Auftrag, der noch nicht aufgelöst ist — sie bleibt, wie sie ist.
 */
export function formatPlanTime(value: string, timeZone: string): string {
  if (/^\d{2}:\d{2}$/.test(value)) return value;
  return formatInstant(new Date(value), timeZone, "de-DE", "datetime");
}

/**
 * Das Ladeziel, das gilt (HM-165): das dieser Sitzung, sonst das wirksame aus
 * Fahrzeug oder Plan. Ein Sitzungsziel `0` heißt in evcc „keins" und kommt hier
 * als `null` an — „Ladeziel 0 %" gibt es nicht mehr.
 */
export function limitInForce(point: ChargePoint): number | null {
  return point.state.limitSoc.value ?? point.state.effectiveLimitSoc.value;
}

/** Eine anstehende Umschaltung in einem Satz — `null`, wenn keine ansteht. */
export function switchingSentence(point: ChargePoint): string | null {
  const { pvAction, pvRemainingS, phaseAction, phaseRemainingS } = point.state.switching;
  const inTime = (seconds: number | null) =>
    seconds === null || seconds <= 0 ? "" : ` in ${formatDuration(seconds)}`;
  if (pvAction.value === "enable")
    return `Laden mit Überschuss beginnt${inTime(pvRemainingS.value)}`;
  if (pvAction.value === "disable")
    return `Laden mit Überschuss endet${inTime(pvRemainingS.value)}`;
  if (phaseAction.value === "scale_1p") {
    return `Umschaltung auf eine Phase${inTime(phaseRemainingS.value)}`;
  }
  if (phaseAction.value === "scale_3p") {
    return `Umschaltung auf drei Phasen${inTime(phaseRemainingS.value)}`;
  }
  return null;
}

/**
 * Der Plan, der gilt, in einer Zeile — der ausdrücklich gesetzte zuerst (am
 * Fahrzeug, dann am Ladepunkt), sonst der wirksame aus evcc, der auch ein
 * wiederkehrender sein kann.
 */
export interface PlanSummary {
  readonly kind: "soc" | "energy" | "effective";
  readonly target: string;
  readonly time: string;
}

export function planSummary(point: ChargePoint): PlanSummary | null {
  const { plan } = point.state;
  if (plan.soc.value !== null && plan.socTime.value !== null) {
    return { kind: "soc", target: `${percentOf(plan.soc.value)} %`, time: plan.socTime.value };
  }
  if (plan.energyWh.value !== null && plan.energyTime.value !== null) {
    return {
      kind: "energy",
      target: `${Math.round(plan.energyWh.value / 100) / 10} kWh`.replace(".", ","),
      time: plan.energyTime.value,
    };
  }
  if (plan.effectiveSoc.value !== null && plan.effectiveTime.value !== null) {
    return {
      kind: "effective",
      target: `${percentOf(plan.effectiveSoc.value)} %`,
      time: plan.effectiveTime.value,
    };
  }
  return null;
}

/** Der Name eines Fahrzeugs aus seiner Nummer — „Fahrzeug 4", wenn es keinen gemeldet hat. */
export function vehicleTitle(point: ChargePoint, number: number): string {
  return (
    point.state.vehicle.choices.find((choice) => choice.number === number)?.title ??
    `Fahrzeug ${number}`
  );
}

const kwh = (wh: number) => `${String(Math.round(wh / 100) / 10).replace(".", ",")} kWh`;

/**
 * Was eine Änderung war, in einem Satz — für den Verlauf und „zuletzt geändert".
 * `plain` nimmt die Wörter von „Laden". Ein Zeitpunkt steht in der Zeitzone der
 * Anlage (`point.timezone`).
 */
export function changeTitle(
  change: ChargePointChange | null,
  plain = false,
  point?: ChargePoint,
): string {
  if (change === null) return "Einstellung am Ladepunkt";
  const timeZone = point?.timezone ?? "Europe/Berlin";
  switch (change.setting) {
    case "mode": {
      const text = modeText(change.value);
      return `Betriebsart: ${plain ? text.plain : text.label}`;
    }
    case "limitSoc":
      return change.value > 0
        ? `Ladeziel ${percentOf(change.value)} %`
        : "Ladeziel der Sitzung zurückgenommen";
    case "minCurrent":
      return `Mindeststrom ${change.value} A`;
    case "maxCurrent":
      return `Maximalstrom ${change.value} A`;
    case "phases":
      return `Phasen ${phaseLabel(change.value)}`;
    case "alwaysCharge":
      return `Immer mit Mindestleistung: ${ALWAYS_CHARGE_TEXTS[change.value].label}`;
    case "limitEnergy":
      return change.value === null ? "Energieziel entfernt" : `Energieziel ${kwh(change.value)}`;
    case "vehicle":
      return change.value === null
        ? "Kein Fahrzeug gewählt"
        : `Fahrzeug: ${point === undefined ? `Nr. ${change.value}` : vehicleTitle(point, change.value)}`;
    case "vehicleLimitSoc":
      return `Ladeziel des Fahrzeugs ${percentOf(change.value)} %`;
    case "vehicleMinSoc":
      return `Mindestladestand ${percentOf(change.value)} %`;
    case "planSoc":
      return change.value === null ? "Plan entfernt" : `Plan: Ziel ${percentOf(change.value)} %`;
    case "planEnergy":
      return change.value === null ? "Plan entfernt" : `Plan: Ziel ${kwh(change.value)}`;
    case "planSocTime":
    case "planEnergyTime":
      return change.value === null
        ? "Plan entfernt"
        : `Fertig um ${formatPlanTime(change.value, timeZone)}`;
    case "priority":
      return `Vorrang ${change.value}`;
    case "batteryBoost":
      return `Hausakku-Boost ${change.value ? "an" : "aus"}`;
    case "batteryBoostLimit":
      return `Hausakku-Boost bis ${percentOf(change.value)} %`;
    case "enableThreshold":
      return `Einschaltschwelle ${change.value} W`;
    case "disableThreshold":
      return `Abschaltschwelle ${change.value} W`;
    case "enableDelay":
      return `Einschaltverzögerung ${formatDuration(change.value)}`;
    case "disableDelay":
      return `Abschaltverzögerung ${formatDuration(change.value)}`;
    case "smartCostLimit":
      return change.value === null
        ? "Günstig laden entfernt"
        : `Günstig laden unter ${change.value}`;
    case "smartFeedInPriorityLimit":
      return change.value === null
        ? "Einspeisevorrang entfernt"
        : `Einspeisevorrang über ${change.value}`;
  }
}

/**
 * **Ohne gemeldeten Ladestand kein Ladeziel** — weder als Zahl noch zur Wahl.
 *
 * Das Ziel ist ein Ladestand des Fahrzeugs, und die Wallbox beendet die Ladung
 * nur, wenn sie ihn kennt. Meldet das Fahrzeug ihn nicht, stünde „Lädt bis
 * 80 %" über einer Ladung, die bei 80 % gar nicht enden kann, und eine Wahl
 * zwischen 60 und 100 % hätte keine Wirkung.
 */
export function showsLimitSoc(point: ChargePoint): boolean {
  return point.state.vehicleSoc.value !== null;
}

/** Der offene Auftrag — der, dessen Quittung noch aussteht. */
export function pendingCommand(point: ChargePoint): ChargePointCommand | null {
  const [latest] = point.commands;
  return latest !== undefined && (latest.status === "pending" || latest.status === "sent")
    ? latest
    : null;
}

/**
 * Der Satz, der die Lage beschreibt — groß oben auf „Laden".
 *
 * Aus gemeldeten Werten, nicht aus Annahmen: „Ladeziel erreicht" nur, wenn
 * **beide** gemeldet sind, Ladestand und Ziel; „pausiert" nur im Modus „Aus".
 * Ein angestecktes Fahrzeug, das im Modus „Nur Sonne" nicht lädt, **wartet**
 * vielleicht auf Überschuss — oder die Wallbox hat einen anderen Grund. Gesagt
 * wird deshalb, was feststeht.
 */
export function chargeSentence(point: ChargePoint): string {
  const { state } = point;
  if (state.measuredAt === null || state.status.value === null) return "Noch kein Stand gemeldet";

  const soc = state.vehicleSoc.value;
  const limit = showsLimitSoc(point) ? limitInForce(point) : null;
  if (state.status.value === "disconnected") return "Kein Fahrzeug angesteckt";
  if (
    state.waitingFor.value === "authorization" ||
    state.waitingFor.value === "disconnect_required"
  ) {
    return WAITING_TEXTS[state.waitingFor.value];
  }
  if (state.status.value === "charging") {
    return limit === null || limit <= 0 ? "Lädt" : `Lädt bis ${percentOf(limit)} %`;
  }
  if (soc !== null && limit !== null && limit > 0 && soc >= limit) return "Ladeziel erreicht";
  if (state.mode.value === "off") return "Ladung pausiert";
  return "Angesteckt, lädt gerade nicht";
}
